import en_US from "./locales/en_US";
import ConfigService from "@/components/service/ConfigService.js";

// English ships in the main bundle; the other bundles are split out and
// fetched only when that locale is actually in use (see loadLocaleMessages).
const bundleLoaders = {
  "de-DE": () => import("./locales/de_DE"),
  "fr-FR": () => import("./locales/fr_FR"),
  "it-IT": () => import("./locales/it_IT"),
};

const localeMap = {
  "en-US": "en-US",
  "de-DE": "de-DE",
  "fr-FR": "fr-FR",
  "it-IT": "it-IT",
  "en-GB": "en-US",
  "en-AU": "en-US",
  "en-NZ": "en-US",
  "en-CA": "en-US",
  "fr-CA": "fr-FR",
  "de-AT": "de-DE",
  en: "en-US",
  de: "de-DE",
  fr: "fr-FR",
  it: "it-IT",
};

// Locales we have a bundle for, in the forms getLocale may produce.
const supported = new Set(["en-US", "de-DE", "fr-FR", "it-IT", "en", "de", "fr", "it"]);

// Which bundle a locale resolves to: exact map entry, else the language's
// canonical entry, else English.
function resolveBundle(locale) {
  try {
    const exact = localeMap[locale];
    if (exact) return exact;
    const byLanguage = localeMap[locale.split("-")[0]];
    if (byLanguage) return byLanguage;
  } catch (ex) {
    console.error(ex);
  }
  return "en-US";
}

/**
 * Resolves to the message bundle for `locale`. English is synchronous (it's
 * in the main bundle); other locales trigger one chunk fetch. Any failure
 * falls back to English rather than leaving the page without messages.
 */
export async function loadLocaleMessages(locale) {
  const bundle = resolveBundle(locale);
  const load = bundleLoaders[bundle];
  if (!load) return en_US;
  try {
    return (await load()).default;
  } catch (ex) {
    console.error(ex);
    return en_US;
  }
}

// The relying party (realnews-web) forwards the user's chosen locale as a
// ?locale=… query param on the authorize redirect. Sign-up/sign-in are native
// form POSTs that bounce back through server redirects (?error=/?message=),
// which drop query params — so the param is persisted in a cookie on first
// sight and the cookie carries the choice across the round trips.
const LOCALE_COOKIE = "ACCOUNT_AUTH_LOCALE";

function normalize(locale) {
  // Accept both en_US and en-US forms; resolve to a bundle we actually have.
  // The region is preserved ("en-GB" stays "en-GB", not "en") so getCountry
  // can read it; getLocaleMessages already falls back to the language bundle.
  const candidate = locale?.replace("_", "-");
  if (!candidate) return null;
  if (supported.has(candidate)) return candidate;
  const language = candidate.split("-")[0];
  return supported.has(language) ? candidate : null;
}

function localeFromQueryParam() {
  try {
    return normalize(new URLSearchParams(window.location.search).get("locale"));
  } catch {
    return null;
  }
}

function localeFromCookie() {
  try {
    const match = document.cookie
      .split("; ")
      .find((entry) => entry.startsWith(`${LOCALE_COOKIE}=`));
    return normalize(match?.split("=")[1]);
  } catch {
    return null;
  }
}

export function getLocale() {
  const fromParam = localeFromQueryParam();
  if (fromParam) {
    try {
      document.cookie = `${LOCALE_COOKIE}=${fromParam}; path=/; max-age=3600; SameSite=Lax`;
    } catch {
      // Cookie write failing just means redirects fall back to the browser language.
    }
    return fromParam;
  }
  const fromCookie = localeFromCookie();
  if (fromCookie) return fromCookie;
  return normalize(navigator.language) || "en-US";
}

export function extractLanguageFromLocale(locale) {
  try {
    if (locale) {
      const res = locale.split("-");
      if (res.length > 0) {
        return res[0];
      }
    }
  } catch (ex) {}
  return "en";
}

export function getLanguage() {
  const locale = getLocale();
  return extractLanguageFromLocale(locale);
}

export async function getCountry() {
    // if it is not available, then use locale of the browser....
    const locale = getLocale();
    try {
      // Chrome/Firefox report bare languages ("it") with no region. Resolve
      // those to the canonical regional locale ("it-IT") via localeMap, as
      // realnews-web does, so the sign-up country defaults to Italy, not US.
      const regional = locale.includes("-") ? locale : localeMap[locale] || locale;
      const res = regional.split("-");
      if (res.length > 1) {
        return res[1];
      }
    } catch (ex) {
      console.error(ex);
    }

    // nothing works, then use 'US'
    return "US";
}
